import { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Theme,
    Box,
    Avatar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import { ReactComponent as PersonIcon } from '../../../../components/person.svg';
import { ReactComponent as PersonDeactivatedIcon } from '../../../../components/person-deactivated.svg';
import theme from '../../../../theme';
import { ApprovalDetails as ApprovalDetailsType, ApprovalTimelineEvent } from '../../../../types';
import { StatusChip } from '../../../../components/StatusChip';
import InactiveUserName, {
    DEACTIVATED_HOVER_AREA_SX,
    DeactivatedHoverLabel,
} from '../../../../components/InactiveUserName';
import { ApprovalStatusMap } from '../constants';

const useStyles = makeStyles()((theme: Theme) => ({
    accordionRoot: {
        backgroundColor: 'transparent',
        marginTop: theme.spacing(1),
        boxShadow: 'none',
        overflow: 'hidden',
        fontFamily: 'Poppins',
    },

    accordionSummary: {
        backgroundColor: theme.palette.primary.light,
        minHeight: '56px',
        padding: '0 16px',
        '& .MuiTypography-root': {
            fontWeight: 500,
            fontSize: '16px',
            color: theme.palette.common.white,
        },
        flexDirection: 'row-reverse',
    },

    accordionDetails: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.primary.light,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },

    requiredApprovalsContainer: {
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '10px',
        padding: '12px 14px',
        maxWidth: '50%',
    },

    requiredApprovalsHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
    },

    requiredApprovalsLabel: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#FFFFFF',
        fontFamily: 'Poppins',
    },

    requiredApprovalsCount: {
        fontSize: '12px',
        fontWeight: 500,
        color: 'rgba(255,255,255,0.55)',
        fontFamily: 'Poppins',
    },

    approvalRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingTop: '8px',
    },

    approvalRowLeft: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },

    approvalRowName: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#FFFFFF',
        fontFamily: 'Poppins',
    },

    approvalRowRole: {
        fontSize: '11px',
        color: 'rgba(255,255,255,0.5)',
        fontFamily: 'Poppins',
    },

    approvalRowRight: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
    },

    approvalStatusPending: {
        fontSize: '12px',
        fontWeight: 500,
        color: theme.palette.secondary.main,
        fontFamily: 'Poppins',
    },

    approvalStatusApproved: {
        fontSize: '12px',
        fontWeight: 500,
        color: theme.palette.success.main,
        fontFamily: 'Poppins',
    },

    approvalStatusChangesRequested: {
        fontSize: '12px',
        fontWeight: 500,
        color: theme.palette.error.main,
        fontFamily: 'Poppins',
    },

    approvalRowTimestamp: {
        fontSize: '11px',
        color: 'rgba(255,255,255,0.6)',
        fontFamily: 'Poppins',
    },

    commentsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '6px',
        padding: '12px',
        fontFamily: 'Poppins',
        maxHeight: '320px',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
            width: '5px',
            height: '5px',
            borderRadius: '20px',
        },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: 20,
            backgroundColor: theme.palette.secondary.dark,
        },
    },

    commentItem: {
        display: 'flex',
        gap: '12px',
        fontFamily: 'Poppins',
    },

    avatar: {
        width: '35px',
        height: '35px',
        fontSize: '12px',
        backgroundColor: theme.palette.info.main,
    },

    commentContent: {
        flex: 1,
        fontFamily: 'Poppins',
    },

    nameRow: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
        fontFamily: 'Poppins',
    },

    commenterName: {
        color: '#FFFFFF',
        fontWeight: 600,
        fontSize: '14px',
        fontFamily: 'Poppins',
    },

    commenterRole: {
        color: 'rgba(255,255,255,0.65)',
        fontSize: '12px',
        fontFamily: 'Poppins',
    },

    commentDate: {
        color: 'rgba(255,255,255,0.65)',
        fontSize: '12px',
        fontFamily: 'Poppins',
    },

    commentText: {
        color: '#FFFFFF',
        fontSize: '14px',
        marginTop: '4px',
        whiteSpace: 'pre-line',
        fontFamily: 'Poppins',
    },
}));

interface Props {
    approvalDetails?: ApprovalDetailsType;
}

const ApprovalDetails = ({ approvalDetails }: Props) => {
    const styles = useStyles();
    const [showAllComments, setShowAllComments] = useState(false);

    const requiredApprovers = [
        ...(approvalDetails?.approvals || [])
            .filter(item => item.isRequired)
            .map((item, index) => ({
                id: index,
                name: item.approver,
                isDeactivated: !!item.approverIsDeactivated,
                role: item.roleCode,
                status: 'Approved' as const,
                timestamp: item.time,
            })),

        ...(approvalDetails?.pendingApprovals || [])
            .filter(item => item.isRequired)
            .map((item, index) => ({
                id: (approvalDetails?.approvals?.length || 0) + index,
                name: item.roleCode,
                isDeactivated: false,
                role: item.roleCode,
                status: 'Pending' as const,
                timestamp: undefined,
            })),

        ...(approvalDetails?.requestChanges || [])
            .filter(item => item.isRequired)
            .map((item, index) => ({
                id:
                    (approvalDetails?.approvals?.length || 0) +
                    (approvalDetails?.pendingApprovals?.length || 0) +
                    index,
                name: item.requester,
                isDeactivated: !!item.requesterIsDeactivated,
                role: item.roleCode,
                status: 'ChangesRequested' as const,
                timestamp: item.time,
            })),
    ];

    const commentEventTypes = ['REQUESTED_CHANGES', 'RESET_DUE_TO_EDIT', 'APPROVED', 'USER_COMMENT', 'SUBMITTED_FOR_REVIEW'];

    const allComments =
        approvalDetails?.approvalTimeline
            .filter((comment: ApprovalTimelineEvent) => commentEventTypes.includes(comment.eventSubType))
            .slice()
            .reverse()
            .map(item => ({
                id: item.id,
                name: item.actedByName,
                isDeactivated: !!item.actedByIsDeactivated,
                role: item.actedByRole,
                comment: item.comment,
                date: item.createdAt,
                eventSubType: item.eventSubType,
            })) || [];

    const visibleComments = showAllComments
        ? allComments
        : allComments.slice(0, 2);

    const approvedCount = requiredApprovers.filter(
        approver => approver.status === 'Approved'
    ).length;

    const getInitials = (name: string) => {
        if (!name) return '';

        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const formatDate = (date?: string) => {
        if (!date) return '';

        return new Date(date).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    return (
        <Accordion defaultExpanded className={styles.classes.accordionRoot}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                className={styles.classes.accordionSummary}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    width="100%"
                    gap={2}
                // justifyContent="space-between"
                >
                    <Typography sx={{ fontFamily: 'Poppins' }}>Approval Details</Typography>
                    {approvalDetails?.payGateStatus &&
                        <StatusChip
                            label={ApprovalStatusMap[approvalDetails?.payGateStatus as keyof typeof ApprovalStatusMap]?.label || approvalDetails?.payGateStatus}
                            color={ApprovalStatusMap[approvalDetails?.payGateStatus as keyof typeof ApprovalStatusMap]?.color || 'default'}
                        />}
                </Box>
            </AccordionSummary>

            <AccordionDetails className={styles.classes.accordionDetails}>
                <Box className={styles.classes.requiredApprovalsContainer}>
                    <Box className={styles.classes.requiredApprovalsHeader}>
                        <Typography className={styles.classes.requiredApprovalsLabel}>
                            Required Approvals
                        </Typography>

                        <Typography className={styles.classes.requiredApprovalsCount}>
                            {approvedCount}/{requiredApprovers.length} Approved
                        </Typography>
                    </Box>

                    {requiredApprovers.length === 0 ? (<Typography
                        sx={{
                            color: 'rgba(255,255,255,0.6)',
                            fontSize: '13px',
                        }}
                    >
                        No approval activity available.
                    </Typography>
                    ) : (
                        requiredApprovers.map(approver => (
                            <Box
                                key={approver.id}
                                className={styles.classes.approvalRow}
                            >
                                <Box
                                    className={styles.classes.approvalRowLeft}
                                    sx={DEACTIVATED_HOVER_AREA_SX}
                                >
                                    <Box display="flex" alignItems="center" gap={1}>
                                        {approver.isDeactivated ? <PersonDeactivatedIcon /> : <PersonIcon />}

                                        <InactiveUserName isInactive={approver.isDeactivated} withTooltip={false}>
                                            <Typography className={styles.classes.approvalRowName}>
                                                {approver.name}
                                            </Typography>
                                        </InactiveUserName>
                                    </Box>

                                    {(approver.role || approver.isDeactivated) && (
                                        <Typography className={styles.classes.approvalRowRole}>
                                            {approver.role}
                                            {approver.isDeactivated && (
                                                <DeactivatedHoverLabel ml={approver.role ? 1 : 0} />
                                            )}
                                        </Typography>
                                    )}
                                </Box>

                                <Box className={styles.classes.approvalRowRight}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        {approver.status === 'Approved' ? (
                                            <TaskAltIcon
                                                sx={{
                                                    fontSize: 16,
                                                    color: theme.palette.success.main,
                                                }}
                                            />
                                        ) : approver.status === 'ChangesRequested' ? (
                                            <WarningAmberOutlinedIcon
                                                sx={{
                                                    fontSize: 16,
                                                    color: theme.palette.error.main,
                                                }}
                                            />
                                        ) : (
                                            <HistoryToggleOffIcon
                                                sx={{
                                                    fontSize: 16,
                                                    color: theme.palette.secondary.main,
                                                }}
                                            />
                                        )}
                                    <Typography
                                        className={
                                            approver.status === 'Approved'
                                                ? styles.classes.approvalStatusApproved
                                                : approver.status ===
                                                    'ChangesRequested'
                                                    ? styles.classes
                                                        .approvalStatusChangesRequested
                                                    : styles.classes
                                                        .approvalStatusPending
                                        }
                                    >
                                        {approver.status === 'ChangesRequested'
                                            ? 'Changes Requested'
                                            : approver.status}
                                    </Typography>
                                    </Box>

                                    {approver.timestamp && (
                                        <Typography
                                            className={
                                                styles.classes.approvalRowTimestamp
                                            }
                                        >
                                            {formatDate(approver.timestamp)}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>

                <Box>
                    <Typography
                        sx={{
                            color: '#FFFFFF',
                            fontWeight: 600,
                            mb: 2,
                        }}
                    >
                        Comments
                    </Typography>

                    {allComments.length === 0 ? (
                        <Typography
                            sx={{
                                color: 'rgba(255,255,255,0.6)',
                                fontSize: '13px',
                            }}
                        >
                            No comments available.
                        </Typography>
                    ) : (
                        <>
                            <Box className={styles.classes.commentsList}>
                                {visibleComments.map((comment) => (
                                    <Box key={comment.id} className={styles.classes.commentItem}>
                                        <Avatar className={styles.classes.avatar}>
                                            {getInitials(comment.name)}
                                        </Avatar>

                                        <Box className={styles.classes.commentContent}>
                                            <Box className={styles.classes.nameRow} sx={DEACTIVATED_HOVER_AREA_SX}>
                                                <InactiveUserName isInactive={comment.isDeactivated} withTooltip={false}>
                                                    <Typography className={styles.classes.commenterName}>
                                                    {comment.name}
                                                </Typography>
                                                </InactiveUserName>

                                                <Typography className={styles.classes.commenterRole}>
                                                    {comment.role}
                                                </Typography>
                                                {comment.isDeactivated && <DeactivatedHoverLabel ml={0} />}
                                            </Box>

                                            <Box className={styles.classes.nameRow}>
                                                <Typography className={styles.classes.commentDate}>
                                                    {formatDate(comment.date)}
                                                </Typography>

                                                {(comment.eventSubType === 'APPROVED' || comment.eventSubType === 'REQUESTED_CHANGES') ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', padding: '0px 4px', '& .MuiChip-root': { height: '16px' }, '& .MuiChip-label': { fontSize: '12px !important', padding: '0px 6px !important' } }}>
                                                        <StatusChip
                                                            label={ApprovalStatusMap[comment.eventSubType as keyof typeof ApprovalStatusMap]?.label || comment.eventSubType}
                                                            color={ApprovalStatusMap[comment.eventSubType as keyof typeof ApprovalStatusMap]?.color || 'default'}
                                                        />
                                                    </Box>
                                                ) : comment.eventSubType === 'RESET_DUE_TO_EDIT' ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', padding: '0px 4px', '& .MuiChip-root': { height: '16px' }, '& .MuiChip-label': { fontSize: '10px !important', padding: '0px 6px !important' } }}>
                                                        <StatusChip
                                                            label={'Auto'}
                                                            color={'default'}
                                                        />
                                                    </Box>) : comment.eventSubType === 'SUBMITTED_FOR_REVIEW' ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', padding: '0px 4px', '& .MuiChip-root': { height: '16px' }, '& .MuiChip-label': { fontSize: '10px !important', padding: '0px 6px !important' } }}>
                                                            <StatusChip
                                                                label={'SUBMITTED_FOR_REVIEW'}
                                                                color={'#0da8c9'}
                                                            />
                                                        </Box>) : null}
                                            </Box>

                                            <Typography className={styles.classes.commentText}>
                                                {comment.comment}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>

                            {allComments.length > 2 && (
                                <Box
                                    display="flex"
                                    justifyContent="flex-start"
                                    mt={1}
                                >
                                    <Typography
                                        sx={{
                                            cursor: 'pointer',
                                            color: '#FFFFFFB3',
                                            fontSize: '12px',
                                        }}
                                        onClick={() =>
                                            setShowAllComments(prev => !prev)
                                        }
                                    >
                                        {showAllComments
                                            ? 'See Less'
                                            : `See ${allComments.length - 2} More`}
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default ApprovalDetails;